import { IconArrowRotateAnticlockwiseOutline18, IconArrowRotateClockwiseOutline18, IconChatBubbleOutline18, IconCheckOutline18, IconCodeBranchOutline18, IconCopyOutline18, IconFilterOutline18, IconMinusOutline18, IconTrashOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import ConfirmDialog from "@/components/ConfirmDialog";
import { PixelLoader } from "@/components/ui/pixel-loader";
import NodeSelectionIndicator from "@/components/live/NodeSelectionIndicator";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from "@/components/ui/context-menu";
import { useDefinitionByType } from "@/hooks/useActionTriggerDefinitions";
import { useFixWithAI } from "@/hooks/useFixWithAI";
import { getStatusLabel, useNodeExecutionStatus } from "@/hooks/useNodeExecutionStatus";
import { cn } from "@/lib/utils";
import { useNodeEditorStore } from "@/stores/nodeEditorStore";
import { useRunExecutionStore } from "@/stores/runExecutionStore";
import { useWorkflowRunsStore } from "@/stores/workflowRunsStore";
import type { WorkflowComment } from "@/types/workflow-comments";
import { resolveDescriptionTemplates } from "@/utils/resolveDescriptionTemplates";
import { Handle, Position, useReactFlow } from "@xyflow/react";
import isEqual from "fast-deep-equal";
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAIHighlight } from "../_hooks/useAIHighlight";
import { useCompactView } from "./CompactViewContext";
import NodeCommentsBadge from "./NodeCommentsBadge";
import { NodeDescriptionToolbar } from "./NodeDescriptionToolbar";
import NodeExecutionDetails, { ErrorDetailModal, type NodeExecutionDetailsHandle } from "./NodeExecutionDetails";
import { useNodeDescriptionVisible, useNodeHoverSetters } from "./NodeHoverContext";
import NodeOutputPreview from "./NodeOutputPreview";
import { getLogicNodeSummary } from "./nodeSummaries";
import { NodeValidationErrorToolbar } from "./NodeValidationErrorToolbar";
import { useMultipleNodesSelected } from "./useMultipleNodesSelected";
import { useNodeValidationErrors } from "./ValidationErrorContext";

/**
 * Calculate duration between two timestamps
 */
const calculateDuration = (start: string | undefined, end: string | undefined): string => {
  if (!start || !end) return "";
  try {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const durationMs = endDate.getTime() - startDate.getTime();
    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
    return `${(durationMs / 60000).toFixed(1)}m`;
  } catch {
    return "";
  }
};
interface LogicNodeProps {
  data: {
    name?: string;
    type?: string;
    description?: string;
    payload?: Record<string, unknown>;
    titleOverride?: string;
    onAddComment?: (nodeId: string) => void;
    onAddNodeComment?: (text: string) => void;
    nodeComments?: WorkflowComment[];
    onDeselectNode?: () => void;
    apiNodeId?: string;
    parentForEachApiNodeId?: string;
    hasWaitBranches?: boolean;
    _refreshKey?: string;
    isStreamingNew?: boolean;
    isStreamingEdit?: boolean;
  };
  selected?: boolean;
  id: string;
}
const LOGIC_ICON_MAP = {
  if: IconCodeBranchOutline18,
  "for-each": IconArrowRotateClockwiseOutline18,
  and: IconFilterOutline18,
  or: IconFilterOutline18,
  not: IconArrowRotateAnticlockwiseOutline18
} as const;

// Memoized for performance - React Flow re-renders nodes frequently
function LogicNodeComponent({
  data,
  selected,
  id
}: LogicNodeProps) {
  const {
    deleteElements,
    getNode
  } = useReactFlow();
  const isCompactView = useCompactView();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const nodeRootRef = useRef<HTMLDivElement>(null);
  const nodeExecutionDetailsRef = useRef<NodeExecutionDetailsHandle>(null);
  const logicType = data.type || "";
  const isIfThenElse = logicType === "if";
  const isWaitWithBranches = logicType === "wait" && !!data.hasWaitBranches;
  const IconComponent = LOGIC_ICON_MAP[logicType as keyof typeof LOGIC_ICON_MAP] ?? IconCodeBranchOutline18;

  // Get the logic action definition
  const definition = useDefinitionByType(logicType);

  // Track hover state globally for description toolbar visibility
  // PERFORMANCE: Use setters-only hook to avoid re-renders when other nodes are hovered
  const {
    setHoveredNodeId,
    setSelectedNodeId
  } = useNodeHoverSetters();
  const {
    showToolbar: showDescriptionToolbar,
    setToolbarHovered,
    canShowSelectedToolbar
  } = useNodeDescriptionVisible(id, isHovered, !!selected);
  const multipleNodesSelected = useMultipleNodesSelected();

  // Get validation errors for this node
  const validationErrors = useNodeValidationErrors(id);
  const isBeingConfigured = useNodeEditorStore(s => s.newlyAddedNodeId) === id;
  const hasValidationErrors = validationErrors.length > 0 && !isBeingConfigured;

  // Update selected node in context when selection changes
  useEffect(() => {
    if (selected) {
      setSelectedNodeId(id);
    } else {
      setSelectedNodeId(null);
      // Clear newly-added flag when user navigates away from this node
      if (useNodeEditorStore.getState().newlyAddedNodeId === id) {
        useNodeEditorStore.getState().setNewlyAddedNodeId(null);
      }
    }
  }, [selected, id, setSelectedNodeId]);

  // "Fix with AI" functionality for failed workflow runs
  const handleFixWithAI = useFixWithAI(data.onDeselectNode);

  // Track AI update highlight effect
  const showAIHighlight = useAIHighlight(data._refreshKey);

  // Get execution status for this node using API node ID (server sends events with API IDs)
  const executionNodeId = data.apiNodeId || id;
  const parentForEachApiNodeId = data.parentForEachApiNodeId;
  const {
    status: executionStatus,
    statusClasses,
    hasActiveRun,
    nodeState,
    runId
  } = useNodeExecutionStatus(executionNodeId, parentForEachApiNodeId);
  const runWorkflowId = useRunExecutionStore(state => state.currentRun?.workflowId);
  const runsStoreWorkflowId = useWorkflowRunsStore(state_0 => state_0.currentWorkflowId);
  const currentWorkflowId = runWorkflowId || runsStoreWorkflowId;

  // Determine if we should show output preview instead of settings
  const showOutputPreview = hasActiveRun && (executionStatus === "success" || executionStatus === "failure") && nodeState?.result && Object.keys(nodeState.result).length > 0;
  const forEachCounts = logicType === "for-each" ? nodeState?.forEachCounts : undefined;
  const forEachOutcomeItems = useMemo(() => {
    if (!forEachCounts) return [];
    const items: Array<{
      key: "succeeded" | "failed";
      count: number;
      label: string;
      icon: typeof IconCheckOutline18;
      className: string;
    }> = [];
    if (forEachCounts.succeeded > 0) {
      items.push({
        key: "succeeded",
        count: forEachCounts.succeeded,
        label: "succeeded",
        icon: IconCheckOutline18,
        className: "text-green-600 dark:text-green-400"
      });
    }
    if (forEachCounts.failed > 0) {
      items.push({
        key: "failed",
        count: forEachCounts.failed,
        label: "failed",
        icon: IconXmarkOutline18,
        className: "text-red-600 dark:text-red-400"
      });
    }
    return items;
  }, [forEachCounts]);
  const showForEachOutcomeSummary = forEachOutcomeItems.length > 0;
  const handleDelete = useCallback(() => {
    // Delay to let ContextMenu fully close before opening Dialog
    // Prevents pointer-events conflicts between the two Radix components
    setTimeout(() => setShowDeleteConfirm(true), 0);
  }, []);
  const confirmDelete = useCallback(() => {
    // Close dialog first, then delete on the next tick so Radix can start
    // its cleanup. However, Radix DismissableLayer saves/restores
    // body pointer-events on mount/unmount. If the Dialog opened while
    // the ContextMenu was still closing, DismissableLayer saves the stale
    // "none" value and restores it on unmount — permanently blocking
    // pointer events. The rAF cleanup below is a safety net for that race.
    setShowDeleteConfirm(false);
    setTimeout(() => {
      deleteElements({
        nodes: [{
          id
        }]
      });
      // Safety: reset body pointer-events after React effects settle
      requestAnimationFrame(() => {
        if (document.body.style.pointerEvents === "none") {
          document.body.style.removeProperty("pointer-events");
        }
      });
    }, 0);
  }, [deleteElements, id]);
  const handleDuplicate = useCallback(() => {
    const node = getNode(id);
    if (!node) return;
    // TODO: Implement duplicate functionality
  }, [getNode, id]);
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    setHoveredNodeId(id);
  }, [id, setHoveredNodeId]);
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setHoveredNodeId(null);
  }, [setHoveredNodeId]);

  // Memoize nodeInfo object to prevent unnecessary re-renders
  const nodeInfo = useMemo(() => ({
    name: data.name,
    type: data.type,
    description: data.description,
    category: "logic" as const
  }), [data.name, data.type, data.description]);

  // Determine if we should show the rotating border effect
  const canShowAnimatedBorder = !showAIHighlight && (!hasActiveRun || executionStatus === "idle") && !hasValidationErrors;
  const showRotatingBorder = selected && canShowAnimatedBorder;
  const showHoverBorder = isHovered && !selected && canShowAnimatedBorder;
  const streamingClass = data.isStreamingNew ? "streaming-enter" : data.isStreamingEdit ? "streaming-edit-highlight" : undefined;
  const resolvedDescription = useMemo(() => resolveDescriptionTemplates(data.description, data.payload), [data.description, data.payload]);
  const logicSummary = useMemo(() => getLogicNodeSummary(data.type, data.payload, {
    getNodeName: nodeId => {
      const node_0 = getNode(nodeId);
      return (node_0?.data as Record<string, unknown>)?.name as string | undefined;
    }
  }), [data.payload, data.type, getNode]);
  return <ContextMenu>
      <ContextMenuTrigger asChild>
        <div ref={nodeRootRef} data-api-node-id={executionNodeId} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={cn("border-ds-neutral-200 bg-ds-surface-secondary rounded-ds-14 group relative w-[280px] cursor-pointer overflow-visible border p-1.5 transition-[border-color,box-shadow,opacity]", streamingClass,
      // Emboss/Deboss effect - deboss during active run (pressed in), emboss otherwise (raised)
      hasActiveRun && executionStatus !== "idle" ? "node-deboss" : "node-emboss",
      // AI update highlight effect
      showAIHighlight && "animate-ai-highlight shadow-ds-elev-2 border-dashed border-blue-500 shadow-blue-500/30 ring-2 ring-blue-500/50",
      // Execution status styles (highest priority when active)
      !showAIHighlight && hasActiveRun && executionStatus !== "idle" && statusClasses,
      // Validation error styles
      !showAIHighlight && !hasActiveRun && hasValidationErrors && "border-dashed border-red-500 bg-red-50/50 shadow-red-500/10 dark:bg-red-950/20",
      // Selection styles (only when not showing execution status or validation errors)
      !showAIHighlight && (!hasActiveRun || executionStatus === "idle") && !hasValidationErrors && (selected ? "node-selected-purple bg-ds-surface-card" : "bg-ds-surface-card border-purple-300 dark:border-purple-700"),
      // Background tints for non-success execution states only
      !showAIHighlight && hasActiveRun && executionStatus === "failure" && "bg-red-50 !backdrop-blur-sm dark:bg-red-950/80", !showAIHighlight && hasActiveRun && executionStatus === "skipped" && "bg-ds-neutral-100 dark:bg-ds-neutral-800 !backdrop-blur-sm", !showAIHighlight && hasActiveRun && executionStatus === "running" && "bg-purple-50 !backdrop-blur-sm dark:bg-purple-950/80", !showAIHighlight && hasActiveRun && executionStatus === "waiting" && "bg-amber-50 !backdrop-blur-sm dark:bg-amber-950/80")}>
          {/* Rotating shiny border effect for selected/hovered nodes */}
          {(showRotatingBorder || showHoverBorder) && <>
              <div className="shimmer-border-purple rounded-ds-lg pointer-events-none absolute -inset-[3px] -z-10 overflow-hidden" />
              <div className="rounded-ds-14 bg-ds-surface-card pointer-events-none absolute -inset-[1px] -z-10" />
            </>}
          {/* Inner card surface */}
          <div className="bg-ds-surface-primary shadow-ds-float-deep rounded-lg px-4 py-3">
            {/* AI Description Toolbar - shows on hover, or on selection when no other node is hovered */}
            <NodeDescriptionToolbar description={data.description} errorMessage={executionStatus === "failure" ? nodeState?.error : undefined} defaultDescription={definition?.description} isVisible={!multipleNodesSelected && (showDescriptionToolbar || isCompactView && canShowSelectedToolbar) && !hasValidationErrors} nodeName={data.name} nodeId={id} onInspect={hasActiveRun && executionStatus !== "idle" ? () => nodeExecutionDetailsRef.current?.openInspectModal() : undefined} onComment={data.onAddComment ? () => data.onAddComment?.(id) : undefined} onDuplicate={handleDuplicate} onDelete={handleDelete} onHoverChange={setToolbarHovered} nodeRef={nodeRootRef} payload={data.payload} />

            {/* Validation Error Toolbar - shows on hover/selection when node has validation errors */}
            <NodeValidationErrorToolbar errors={validationErrors} isVisible={hasValidationErrors && (isHovered || !!selected)} nodeName={data.name} nodeId={id} nodeRef={nodeRootRef} onDeselectNode={data.onDeselectNode} />

            {/* Node selection indicator - shows when other users have this node selected */}
            <NodeSelectionIndicator nodeId={id} />

            {/* Execution status badge - icon only */}
            {hasActiveRun && executionStatus !== "idle" && (() => {
            const statusColorStyles: Partial<Record<string, string>> = {
              running: "border-purple-500 bg-purple-100 text-purple-600 dark:bg-purple-900/50 dark:text-purple-400",
              success: "border-green-500 bg-green-100 text-green-600 dark:border-green-500 dark:bg-green-900/50 dark:text-green-400",
              failure: "border-red-400 bg-red-100 text-red-600 dark:border-red-500 dark:bg-red-900/50 dark:text-red-400",
              skipped: "border-ds-border-default bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-800 dark:text-ds-text-disabled"
            };
            const forEachProgress = nodeState?.forEachProgress;
            const hasForEachProgress = !!forEachProgress && typeof forEachProgress.total === "number" && forEachProgress.total > 0;
            const iterationLabel = hasForEachProgress ? `${forEachProgress.current + 1}/${forEachProgress.total}` : null;
            return <div className="absolute -left-3 -top-3 z-20">
                    <div role="status" aria-label={`Node status: ${getStatusLabel(executionStatus)}${hasForEachProgress ? `, iteration ${forEachProgress.current + 1} of ${forEachProgress.total}` : ""}`} onClick={executionStatus === "failure" ? e => {
                e.stopPropagation();
                setErrorModalOpen(true);
              } : undefined} className={cn("shadow-ds-soft flex h-6 items-center justify-center rounded-full border-2 backdrop-blur-md", statusColorStyles[executionStatus], hasForEachProgress ? "gap-1 px-2" : "w-6", executionStatus === "failure" && "cursor-pointer hover:bg-red-200 dark:hover:bg-red-900/70")}>
                      {executionStatus === "running" && <PixelLoader size="xs" aria-label="Running" />}
                      {executionStatus === "success" && <IconCheckOutline18 className="h-3 w-3" aria-hidden="true" />}
                      {executionStatus === "failure" && <IconXmarkOutline18 className="h-3 w-3" aria-hidden="true" />}
                      {executionStatus === "skipped" && <IconMinusOutline18 className="h-3 w-3" aria-hidden="true" />}
                      {iterationLabel && <span className="text-3xs font-semibold leading-none">{iterationLabel}</span>}
                    </div>
                  </div>;
          })()}

            {/* Input Handle - Top Center */}
            <Handle type="target" position={Position.Top} className="!border-ds-neutral-300 !bg-ds-surface-card !h-3 !w-3 !border-2 dark:!border-zinc-600" />

            {/* Node Header */}
            <div className={cn("flex items-center gap-3", !isCompactView && "mb-2")}>
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <IconComponent className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="body-sm-medium truncate">
                  {resolveDescriptionTemplates(data.titleOverride, data.payload) || data.name || data.type || "Logic"}
                </div>
                {!isCompactView && <div className="micro-label flex h-5 items-center gap-1">
                    <span>{showOutputPreview ? "Logic output" : "Logic"}</span>
                    {showOutputPreview && <>
                        <span>·</span>
                        <span className={cn(executionStatus === "success" && "text-green-600 dark:text-green-400", executionStatus === "failure" && "text-red-600 dark:text-red-400")}>
                          {calculateDuration(nodeState?.startedAt, nodeState?.finishedAt) || "Done"}
                        </span>
                      </>}
                  </div>}
              </div>
            </div>

            {!isCompactView && <>
                {/* Description - hide when showing output preview */}
                {!showOutputPreview && (logicSummary || data.description) && <div className="space-y-2 border-t border-purple-200 pt-2 dark:border-purple-800">
                    {logicSummary && <div className="label-xs leading-5">{logicSummary.summary}</div>}
                    {logicSummary?.auxiliary && <div>{logicSummary.auxiliary}</div>}
                    {data.description && <p className="label-xs line-clamp-2 text-purple-700 dark:text-purple-300" title={resolvedDescription}>
                        {resolvedDescription}
                      </p>}
                  </div>}

                {/* Output preview when execution completes */}
                {showOutputPreview && <NodeOutputPreview result={nodeState?.result} status={executionStatus} />}

                {/* Execution input/output details */}
                {hasActiveRun && executionStatus !== "idle" && executionStatus !== "running" && <NodeExecutionDetails ref={nodeExecutionDetailsRef} nodeState={nodeState} nodeInfo={nodeInfo} onFixWithAI={handleFixWithAI} workflowId={currentWorkflowId} runId={runId} />}
              </>}

            {/* Node Comments Badge */}
            {data.nodeComments && <NodeCommentsBadge nodeComments={data.nodeComments} onAddComment={data.onAddNodeComment} />}

            {/* If-Then-Else: Two side-by-side output handles */}
            {isIfThenElse ? <>
                {/* Left Handle - Else */}
                <Handle type="source" position={Position.Left} id="source-else" className="!bg-ds-surface-card !-left-1.5 !h-3 !w-3 !border-2 !border-red-500 dark:!border-red-400" style={{
              top: "50%"
            }} />
                <div className="shadow-ds-soft pointer-events-none absolute -left-14 top-1/2 -translate-y-1/2 rounded-full bg-red-500 px-3 py-1 text-xs font-bold text-white dark:bg-red-600">
                  Else
                </div>

                {/* Right Handle - Then */}
                <Handle type="source" position={Position.Right} id="source-then" className="!bg-ds-surface-card !-right-1.5 !h-3 !w-3 !border-2 !border-green-500 dark:!border-green-400" style={{
              top: "50%"
            }} />
                <div className="shadow-ds-soft pointer-events-none absolute -right-14 top-1/2 -translate-y-1/2 rounded-full bg-green-500 px-3 py-1 text-xs font-bold text-white dark:bg-green-600">
                  Then
                </div>
              </> : isWaitWithBranches ? <>
                {/* Left Handle - Timed Out */}
                <Handle type="source" position={Position.Left} id="source-timed_out" className="!-left-1.5 !h-3 !w-3 !border-2 !border-amber-400 !bg-amber-100 dark:!border-amber-600 dark:!bg-amber-900" style={{
              top: "50%"
            }} />
                <div className="pointer-events-none absolute -left-[4.5rem] top-1/2 -translate-y-1/2 rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 backdrop-blur-sm dark:bg-amber-900/50 dark:text-amber-300">
                  Timed Out
                </div>

                {/* Right Handle - Resumed */}
                <Handle type="source" position={Position.Right} id="source-resumed" className="!-right-1.5 !h-3 !w-3 !border-2 !border-green-400 !bg-green-100 dark:!border-green-600 dark:!bg-green-900" style={{
              top: "50%"
            }} />
                <div className="pointer-events-none absolute -right-[4.5rem] top-1/2 -translate-y-1/2 rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 backdrop-blur-sm dark:bg-green-900/50 dark:text-green-300">
                  Resumed
                </div>
              </> : (/* Single Bottom Output for other logic types */
          <Handle type="source" position={Position.Bottom} className="!border-ds-neutral-300 !bg-ds-surface-card !h-3 !w-3 !border-2 dark:!border-zinc-600" />)}

            {showForEachOutcomeSummary && <div className="micro-label label pointer-events-none absolute -bottom-5 left-0 flex items-center gap-1.5 font-semibold leading-none">
                {forEachOutcomeItems.map((item, index) => {
              const Icon = item.icon;
              return <Fragment key={item.key}>
                      {index > 0 && <span className="dark:text-ds-text-subtle text-zinc-300">|</span>}
                      <span className={cn("flex items-center gap-1", item.className)}>
                        <Icon className="h-3 w-3" aria-hidden="true" />
                        <span>
                          {item.count} {item.label}
                        </span>
                      </span>
                    </Fragment>;
            })}
              </div>}
          </div>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        <ContextMenuItem onClick={handleDuplicate} className="cursor-pointer">
          <IconCopyOutline18 className="mr-2 h-4 w-4" />
          Duplicate
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={() => data.onAddComment?.(id)} className="cursor-pointer">
          <IconChatBubbleOutline18 className="mr-2 h-4 w-4" />
          Add Comment
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={handleDelete} className="cursor-pointer text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400">
          <IconTrashOutline18 className="mr-2 h-4 w-4" />
          Delete Logic Node
        </ContextMenuItem>
      </ContextMenuContent>
      <ConfirmDialog isOpen={showDeleteConfirm} title="Delete Logic Node" message="Are you sure you want to delete this logic node? This action cannot be undone." confirmText="Delete" cancelText="Cancel" variant="danger" onConfirm={confirmDelete} onCancel={() => setShowDeleteConfirm(false)} />
      {/* Error Detail Modal - opens when clicking on failed status badge */}
      <ErrorDetailModal open={errorModalOpen} onOpenChange={setErrorModalOpen} nodeInfo={nodeInfo} nodeState={nodeState} nodeConfig={data.payload} onFixWithAI={handleFixWithAI} workflowId={currentWorkflowId} runId={runId} />
    </ContextMenu>;
}

// Custom comparator for React.memo - shallow compare primitives, deep compare objects
function arePropsEqual(prevProps: LogicNodeProps, nextProps: LogicNodeProps): boolean {
  if (prevProps.id !== nextProps.id || prevProps.selected !== nextProps.selected) {
    return false;
  }
  const prevData = prevProps.data;
  const nextData = nextProps.data;
  if (prevData === nextData) return true;
  if (!prevData || !nextData) return false;
  if (prevProps.data.isStreamingNew !== nextProps.data.isStreamingNew || prevProps.data.isStreamingEdit !== nextProps.data.isStreamingEdit) {
    return false;
  }
  return prevData.name === nextData.name && prevData.type === nextData.type && prevData.description === nextData.description && prevData.hasWaitBranches === nextData.hasWaitBranches && isEqual(prevData.payload, nextData.payload) && isEqual(prevData.nodeComments, nextData.nodeComments);
}

// React.memo prevents re-renders when props haven't changed
// This is important for React Flow performance with many nodes
const LogicNode = memo(LogicNodeComponent, arePropsEqual);
export default LogicNode;